import React, { FunctionComponent, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, Header, Form, Message, Card } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { toast } from 'react-toastify';

import { ClassProp } from 'helpers/types';
import LoginManager from 'helpers/loginManager';
import Loading from 'components/Loading';
import styles from 'components/UserMgmt/UserMgmt.styles';
import FormInput from 'components/FormComponents/FormInput';
import ToastContainer, { ToastType } from 'components/ToastContainer';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
};

type LoginFormData = {
  verificationCode: string;
};

const MFAForm: FunctionComponent<Props> = ({ classes }) => {
  const methods = useForm<LoginFormData>({
    mode: 'onBlur',
  });
  const [isSubmitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<string>();

  const { handleSubmit, register, watch } = methods;

  const watchedVerificationCode = watch('verificationCode');

  const onVerificationCodeSubmit = async ({
    verificationCode,
  }: LoginFormData) => {
    setSubmitting(true);
    try {
      await LoginManager.getInstance().sendVerificationCode(verificationCode);
    } catch (error) {
      setErrors('That code was invalid, please try again');
    } finally {
      setSubmitting(false);
    }
  };

  const resendCode = async () => {
    setErrors(undefined);
    setSubmitting(true);
    const {
      status,
      message,
    } = await LoginManager.getInstance().reSendVerificationCode();
    toast(
      () => (
        <ToastContainer
          type={status as ToastType}
          title="Verification Code"
          description={message}
        />
      ),
      { hideProgressBar: true, closeButton: false },
    );
    setSubmitting(false);
  };

  return (
    <>
      <Header className={classes.header} as="h2">
        Security verification
      </Header>
      Please enter the verification code sent to the email account associated
      with this user.
      <Card className={classes.container} fluid centered>
        <Card.Content>
          {isSubmitting && <Loading loading />}
          <FormProvider {...methods}>
            <Form
              onSubmit={handleSubmit(onVerificationCodeSubmit)}
              error={!!errors}
            >
              {errors && <Message error content={errors} />}

              <Form.Field className={classes.field}>
                <FormInput
                  type="text"
                  label="Verification Code"
                  register={register()}
                  name="verificationCode"
                  placeholder=""
                />
              </Form.Field>
              <Form.Field className={classes.field}>
                <Button
                  disabled={
                    !watchedVerificationCode ||
                    watchedVerificationCode.length !== 6
                  }
                  className={classes.button}
                  fluid
                  type="submit"
                >
                  Submit
                </Button>
              </Form.Field>
              <Button
                type="button"
                className={classes.backNavLink}
                onClick={resendCode}
              >
                Resend Verification Code?
              </Button>
            </Form>
          </FormProvider>
        </Card.Content>
      </Card>
    </>
  );
};

export default withStyles(styles)(MFAForm);
